import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SigneDirecteurItem } from '../../data/languages/kama/signeDirecteurPractice';
import HieroglyphCard from './HieroglyphCard';
import './KamaLearnMode.css';

const QUESTION_SECONDS = 10;

interface KamaSigneDirecteurExerciseProps {
  title: string;
  items: SigneDirecteurItem[];
  onBackToCategories: () => void;
  onExerciseComplete: () => void;
  onAnswer?: (correct: boolean) => void;
  // Only the Kama "Quiz" mode (graphieReconstructionPractice) gets the
  // countdown, matching QuizComponent's per-question chrono — the
  // "Exercice" mode (signeDirecteurPractice, untimed practice/drilling)
  // reuses this same shell without it.
  timed?: boolean;
}

// Kama-only replacement for the generic ExerciseMode/QuizComponent in the
// "Pratiquer" tab (App.tsx) — a typed-translation exercise makes no sense
// for a language whose whole point is reading signs, not typing them.
// Generic over `items`/`title` so "Exercices" (identifying the signe
// directeur, signeDirecteurPractice.ts) and "Quiz" (reconstructing the
// right sign order, graphieReconstructionPractice.ts) can share this same
// sequential-QCM shell instead of duplicating it — they drill different
// skills, not the same one twice. Reuses HieroglyphCard's own quiz
// rendering (question hidden until answered, single attempt, correct
// answer always highlighted) rather than building a second QCM UI.
const KamaSigneDirecteurExercise: React.FC<KamaSigneDirecteurExerciseProps> = ({
  title,
  items,
  onBackToCategories,
  onExerciseComplete,
  onAnswer,
  timed = false,
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const isDone = index >= items.length;
  const current = items[index];

  const goNext = () => setIndex((i) => Math.min(i + 1, items.length));
  const goPrev = () => setIndex((i) => Math.max(i - 1, 0));
  const restart = () => setIndex(0);

  const [timeLeft, setTimeLeft] = useState(QUESTION_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  // Tracks whether the *current* question has been answered — the timer
  // must stop the instant it has (it was previously ignoring this and
  // ticking straight through to 0 in the background even after a correct
  // answer, silently skipping ahead under the user).
  const [answered, setAnswered] = useState(false);

  // Resets on every question change; counts down to 0 and moves on by
  // itself, same as QuizComponent's timeout-is-a-miss behaviour.
  useEffect(() => {
    setAnswered(false);
    if (!timed || isDone) return;
    setTimeLeft(QUESTION_SECONDS);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          goNext();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timed, index, isDone]);

  const handleAnswered = (correct: boolean) => {
    setAnswered(true);
    clearInterval(intervalRef.current);
    onAnswer?.(correct);
  };

  return (
    <div className="kama-lesson">
      <div className="kama-lesson__header">
        <span className="kama-lesson__category">{title}</span>
        <button type="button" className="kama-lesson__exit" onClick={onBackToCategories}>
          {t('kamaLearnMode.quit')}
        </button>
      </div>

      <div className="kama-lesson__progress">
        <div className="kama-lesson__progressbar-track">
          <div
            className="kama-lesson__progressbar-fill"
            style={{ width: `${(Math.min(index, items.length) / items.length) * 100}%` }}
          />
        </div>
        <span className="kama-lesson__progress-count">
          {Math.min(index + 1, items.length)} / {items.length}
        </span>
      </div>

      {timed && !isDone && (
        <div className="quiz-timer-row">
          <div className="quiz-timer-track">
            <div
              className="quiz-timer-fill"
              style={{
                width: `${(timeLeft / QUESTION_SECONDS) * 100}%`,
                background: timeLeft <= 3 ? 'var(--color-error)' : 'var(--color-accent, var(--gold-primary))',
                transition: 'width 0.9s linear, background 0.3s',
              }}
            />
          </div>
          <span className="quiz-timer-count" style={{ color: timeLeft <= 3 ? 'var(--color-error)' : 'var(--color-accent, var(--gold-primary))' }}>
            {timeLeft}
          </span>
        </div>
      )}

      {!isDone && current ? (
        <>
          <div className="kama-lesson__card-slot">
            <HieroglyphCard
              entryKey={current.key}
              word={current.word}
              gardinerSigns={[]}
              crossReferences={[]}
              onAnswered={handleAnswered}
            />
          </div>
          <div className="kama-lesson__nav">
            {/* Quiz mode: no going back to a scored question, and no
                skipping ahead of one you haven't answered yet — matches
                QuizComponent's linear, single-attempt-per-question flow. */}
            {!timed && (
              <button type="button" className="kama-lesson__nav-btn" onClick={goPrev} disabled={index === 0}>
                {t('kamaLearnMode.previous')}
              </button>
            )}
            <button
              type="button"
              className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
              onClick={goNext}
              disabled={timed && !answered}
            >
              {index === items.length - 1 ? t('kamaLearnMode.finish') : t('kamaLearnMode.next')}
            </button>
          </div>
        </>
      ) : (
        <div className="kama-lesson__done">
          <p className="kama-lesson__done-text">
            {t('kamaLearnMode.trainingDone', { count: items.length })}
          </p>
          <button type="button" className="kama-lesson__nav-btn kama-lesson__nav-btn--primary" onClick={restart}>
            {t('kamaLearnMode.restart')}
          </button>
          <button type="button" className="kama-lesson__nav-btn" onClick={onExerciseComplete}>
            {t('kamaLearnMode.quitTraining')}
          </button>
        </div>
      )}
    </div>
  );
};

export default KamaSigneDirecteurExercise;
